import React, { Component } from 'react';
import subscriptionsService from '../../services/subscriptions';
import styles from './styles.module.scss';

class SubscriptionSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            subscriptionData: null,
            error: null
        };
    }

    componentDidMount() {
        this.fetchSubscriptionStatus();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.currentOrganization?.org_id !== this.props.currentOrganization?.org_id) {
            this.fetchSubscriptionStatus();
        }
    }

    fetchSubscriptionStatus = async () => {
        const organizationId = this.props.currentOrganization?.org_id;
        if (!organizationId) {
            this.setState({ loading: false });
            return;
        }

        try {
            this.setState({ loading: true, error: null });
            const data = await subscriptionsService.getSubscriptionStatus(organizationId);
            this.setState({
                subscriptionData: data,
                loading: false
            });
        } catch (err) {
            console.error('Error fetching subscription status:', err);
            this.setState({
                error: 'Unable to load subscription information',
                loading: false
            });
        }
    };

    handleManageSubscription = async () => {
        const organizationId = this.props.currentOrganization?.org_id;
        if (!organizationId) return;

        try {
            const response = await subscriptionsService.createPortalSession(organizationId);
            if (response.url) {
                window.open(response.url, '_blank');
            }
        } catch (err) {
            console.error('Error opening portal:', err);
            this.setState({
                error: 'Unable to open subscription management'
            });
        }
    };

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    renderSubscriptionContent = () => {
        const { subscriptionData } = this.state;

        // No subscription exists - show Free tier
        if (!subscriptionData || !subscriptionData.hasSubscription) {
            return (
                <div className={styles.content}>
                    <div className={styles.tier}>
                        <span className={styles.tierLabel}>Current Plan:</span>
                        <span className={styles.tierValue}>Free</span>
                    </div>
                    <p className={styles.description}>
                        Upgrade to a paid plan to unlock premium features.
                    </p>
                </div>
            );
        }

        const { hasActiveBenefits, status, currentPeriodEnd, cancelAtPeriodEnd, cancelAt } = subscriptionData;

        // Check if subscription is scheduled to cancel (Stripe uses cancel_at timestamp)
        const isScheduledToCancel = cancelAt || cancelAtPeriodEnd;

        // If no active benefits, treat as Free (unless it recently ended)
        if (!hasActiveBenefits) {
            // No current_period_end means incomplete/never activated - show Free
            if (!currentPeriodEnd) {
                return (
                    <div className={styles.content}>
                        <div className={styles.tier}>
                            <span className={styles.tierLabel}>Current Plan:</span>
                            <span className={styles.tierValue}>Free</span>
                        </div>
                        <p className={styles.description}>
                            Upgrade to a paid plan to unlock premium features.
                        </p>
                    </div>
                );
            }

            // Subscription ended - check if recently
            const endDate = new Date(currentPeriodEnd);
            const now = new Date();

            // Show "benefits ended" if it ended recently (within last 30 days)
            // Otherwise just show Free tier
            const daysSinceEnded = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));

            if (daysSinceEnded > 30) {
                return (
                    <div className={styles.content}>
                        <div className={styles.tier}>
                            <span className={styles.tierLabel}>Current Plan:</span>
                            <span className={styles.tierValue}>Free</span>
                        </div>
                        <p className={styles.description}>
                            Upgrade to a paid plan to unlock premium features.
                        </p>
                    </div>
                );
            }

            // Recently ended - show what happened
            let statusMessage = '';
            let statusClass = styles.inactive;

            if (status === 'past_due') {
                statusMessage = `Payment failed. Benefits ended: ${this.formatDate(currentPeriodEnd)}`;
                statusClass = styles.error;
            } else {
                statusMessage = `Benefits ended: ${this.formatDate(currentPeriodEnd)}`;
            }

            return (
                <div className={styles.content}>
                    <div className={styles.tier}>
                        <span className={styles.tierLabel}>Previous Plan:</span>
                        <span className={styles.tierValue}>
                            {/* TODO: Replace with actual product name once plan_id is added to database */}
                            Pro
                        </span>
                    </div>
                    <div className={`${styles.statusMessage} ${statusClass}`}>
                        {statusMessage}
                    </div>
                    <button
                        className={styles.manageButton}
                        onClick={this.handleManageSubscription}
                    >
                        Reactivate Subscription
                    </button>
                </div>
            );
        }

        // Active subscription with benefits
        let statusMessage = null;
        let statusClass = '';

        if (hasActiveBenefits && !isScheduledToCancel) {
            // Active subscription with recurring payments
            statusMessage = `Next payment: ${this.formatDate(currentPeriodEnd)}`;
            statusClass = styles.active;
        } else if (hasActiveBenefits && isScheduledToCancel) {
            // Scheduled to cancel but still has benefits until cancel date
            const cancelDate = cancelAt || currentPeriodEnd;
            statusMessage = `Cancels on: ${this.formatDate(cancelDate)}`;
            statusClass = styles.warning;
        }

        return (
            <div className={styles.content}>
                <div className={styles.tier}>
                    <span className={styles.tierLabel}>Current Plan:</span>
                    <span className={styles.tierValue}>
                        {/* TODO: Replace with actual product name once plan_id is added to database */}
                        Pro
                    </span>
                </div>
                {statusMessage && (
                    <div className={`${styles.statusMessage} ${statusClass}`}>
                        {statusMessage}
                    </div>
                )}
                <button
                    className={styles.manageButton}
                    onClick={this.handleManageSubscription}
                >
                    Manage Subscription
                </button>
            </div>
        );
    };

    render() {
        const { loading, error, subscriptionData } = this.state;

        // Don't render anything if subscriptions are not enabled for this org
        if (!loading && subscriptionData && subscriptionData.enabled === false) {
            return null;
        }

        if (loading) {
            return (
                <div className={styles.section}>
                    <h2>Subscription</h2>
                    <div className={styles.content}>Loading...</div>
                </div>
            );
        }

        return (
            <div className={styles.section}>
                <h2>Subscription</h2>
                {error && <div className={styles.errorAlert}>{error}</div>}
                {this.renderSubscriptionContent()}
            </div>
        );
    }
}

export default SubscriptionSection;
